/**
 * Proactive "hold the window scroll for a short burst of animation frames"
 * helper for MathEditor. Extracted from `MathEditor.tsx` so the re-entrancy
 * contract below is unit testable — jsdom cannot render MathLive, but the hold
 * itself is pure bookkeeping over scrollX/scrollY and requestAnimationFrame.
 *
 * Why it exists: iOS 26 scrolls the *document* on each input (caret
 * scroll-into-view, miscalculated under the canvas CSS transform). The
 * scroll-lock listener only reacts to the `scroll` event, which iOS fires AFTER
 * the frame paints — so the jump is briefly visible. rAF runs BEFORE paint, so
 * re-asserting the saved position every frame reverts iOS's scroll before it is
 * shown.
 */
/**
 * Build the singleton hold for `win`. Call the returned function from the
 * input/keystroke handlers; it captures the window position at call time
 * (the window is at its resting position then) and pins it for `frames` frames.
 *
 * A RE-ENTRANT call while a hold is already running KEEPS the original saved
 * position and only restarts the frame counter — the same contract as
 * `installWinLock` ("already active → keep the original pre-jump position;
 * don't recapture").
 *
 * This is not cosmetic: `preserveScroll()` arms a hold and then runs
 * `mf.insert()`, which dispatches `input` SYNCHRONOUSLY, and the math-field's
 * input listener calls the hold again. Recapturing there would pin whatever
 * position iOS had already scrolled to inside `insert()` — turning the hold
 * from a jump-preventer into a jump-holder for the next ~24 frames. Rapid
 * typing had the same flaw before the keypad path existed.
 */
export function createWindowScrollHold(win) {
    var raf = 0;
    var pos = { x: 0, y: 0 };
    return function holdWindowScrollBriefly(frames) {
        if (frames === void 0) { frames = 24; }
        // Only capture when no hold is running — a re-entrant call must not
        // recapture the already-jumped position.
        if (!raf)
            pos = { x: win.scrollX, y: win.scrollY };
        var x = pos.x, y = pos.y;
        if (raf)
            win.cancelAnimationFrame(raf);
        var n = 0;
        var tick = function () {
            if (win.scrollX !== x || win.scrollY !== y)
                win.scrollTo(x, y);
            n += 1;
            raf = n < frames ? win.requestAnimationFrame(tick) : 0;
        };
        raf = win.requestAnimationFrame(tick);
    };
}
